import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Video, Image as ImageIcon, LayoutGrid, FolderInput } from "lucide-react";
import { api, MONTHS } from "@/lib/api";
import { useLiveReload } from "@/lib/live";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import PostDetailDialog from "@/components/PostDetailDialog";
import GoalsDialog from "@/components/GoalsDialog";
import MonthGrid from "@/components/MonthGrid";
import RhythmTable from "@/components/RhythmTable";
import AutofillButton from "@/components/AutofillButton";
import ShootSection from "@/components/ShootSection";
import ClientLogo from "@/components/ClientLogo";

function GoalCard({ icon: Icon, label, g, id }) {
  const pct = g.goal ? Math.min(100, Math.round((g.done / g.goal) * 100)) : 0;
  const planPct = g.goal ? Math.min(100 - pct, Math.round((g.planned / g.goal) * 100)) : 0;
  return (
    <div className="rounded-xl border border-border bg-card p-6 fade-up" data-testid={`goal-card-${id}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4"><Icon className="h-4 w-4" />{label}</div>
      <p className="font-display text-4xl font-semibold mb-1" data-testid={`goal-progress-${id}`}>{g.done}<span className="text-muted-foreground text-2xl"> / {g.goal}</span></p>
      <p className="text-sm mb-5" data-testid={`goal-missing-${id}`}>
        {g.missing === 0 ? <span className="text-teal-400">Ziel erreicht</span> : <span className="text-amber-400">{g.missing} fehlen noch</span>}
        {g.planned > 0 && <span className="text-muted-foreground"> · {g.planned} geplant</span>}
      </p>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden flex">
        <div className="bg-teal-400 transition-all duration-700" style={{ width: `${pct}%` }} />
        <div className="bg-teal-400/30 transition-all duration-700" style={{ width: `${planPct}%` }} />
      </div>
    </div>
  );
}

const Eyebrow = ({ children }) => <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-teal-400 mb-4">{children}</p>;

export default function Dashboard() {
  const { clientId, client } = useClient();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard", { params: { client_id: clientId } }).then((r) => setData(r.data));
    api.get("/posts", { params: { client_id: clientId } }).then((r) => setPosts(r.data));
    api.get("/files", { params: { client_id: clientId } }).then((r) => setFiles(r.data));
  }, [clientId]);
  useEffect(() => { load(); }, [load, client]);
  useLiveReload(load);
  useEffect(() => { window.addEventListener("posts-changed", load); return () => window.removeEventListener("posts-changed", load); }, [load]);

  if (!data) return <p className="text-muted-foreground">Lädt…</p>;
  const [y, m] = data.month.split("-");
  const cursor = new Date(+y, +m - 1, 1);
  const pending = posts.filter((p) => p.status === "pending_approval");

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="flex items-center gap-3 mb-4" data-testid="dashboard-client-name"><ClientLogo client={client} className="h-8" fallbackClass="font-mono text-xs uppercase tracking-[0.3em] text-slate-400" testId="dashboard-client-logo" /><span className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">· Redaktionsplan</span></div>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight leading-none" data-testid="dashboard-title">{MONTHS[+m - 1]} <span className="text-teal-400">{y}</span></h1>
          <div className="h-1 w-24 bg-teal-400 mt-6" />
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <GoalsDialog onSaved={load} />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 mb-14">
        <section className="min-w-0">
          <Eyebrow>Monatsübersicht · 1. bis {new Date(+y, +m, 0).getDate()}. {MONTHS[+m - 1]}</Eyebrow>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0"><div className="min-w-[640px]"><MonthGrid cursor={cursor} posts={posts} files={files} rhythm={data.posting_rhythm} onPostClick={setOpen} compact /></div></div>
        </section>
        <aside className="space-y-10">
          <ShootSection />
          <div>
            <Eyebrow>Wartet auf Freigabe</Eyebrow>
            <p className="font-display text-5xl font-semibold mb-4" data-testid="pending-count">{pending.length}</p>
            <div className="space-y-2">
              {pending.slice(0, 4).map((p) => (
                <button key={p.id} onClick={() => setOpen(p)} className="w-full text-left rounded-lg border border-amber-800/40 bg-amber-950/20 px-4 py-3 text-sm hover:bg-amber-950/40 transition-colors" data-testid={`pending-post-${p.id}`}>
                  <span className="block truncate">{p.title || "Ohne Titel"}</span>
                  <span className="text-xs text-slate-500">{new Date(p.scheduled_at).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit" })}</span>
                </button>
              ))}
              {pending.length > 0 && <Link to="/freigaben" className="text-sm text-teal-400 hover:underline" data-testid="goto-approvals-link">Alle Freigaben ansehen →</Link>}
              {pending.length === 0 && <p className="text-sm text-muted-foreground">Alles freigegeben.</p>}
            </div>
          </div>
          {isAdmin && data.unassigned > 0 && (
            <div className="rounded-xl border border-purple-500/30 bg-purple-500/[0.06] p-5" data-testid="unassigned-card">
              <p className="flex items-center gap-2 text-sm font-medium text-purple-300 mb-2"><FolderInput className="h-4 w-4" />Neu im Drive</p>
              <p className="text-sm text-slate-300 mb-1"><span className="font-semibold text-white" data-testid="unassigned-count">{data.unassigned}</span> Dateien sind noch keinem Post zugeordnet.</p>
              <p className="text-xs text-slate-500 mb-4">Videos kommen auf die nächsten freien Video-Tage, Fotos auf die Foto-Tage – genau so viele, wie Dateien da sind.</p>
              <AutofillButton label="Zuordnen & einplanen" onDone={load} testId="autofill-unassigned-button" />
            </div>
          )}
        </aside>
      </div>

      <section className="mb-14">
        <Eyebrow>Posting-Rhythmus</Eyebrow>
        <RhythmTable rhythm={data.posting_rhythm} />
        {data.notes.length > 0 && <ul className="text-sm text-slate-400 space-y-1 mt-4" data-testid="client-notes">{data.notes.map((n, i) => <li key={i}>· {n}</li>)}</ul>}
      </section>

      <section>
        <Eyebrow>Monatsziele</Eyebrow>
        <div className="grid sm:grid-cols-3 gap-4">
          <GoalCard icon={Video} label="Videos / Reels" g={data.goals.videos} id="videos" />
          <GoalCard icon={ImageIcon} label="Fotos" g={data.goals.photos} id="photos" />
          <GoalCard icon={LayoutGrid} label="Posts gesamt" g={data.goals.posts} id="posts" />
        </div>
      </section>
      {open && <PostDetailDialog post={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
